/**
 * Test Monitoring and Cheating Detection Utility
 * Tracks user behavior during tests and logs suspicious activities
 */

export const CHEATING_TYPES = {
    TAB_SWITCH: 'tab_switch',
    COPY_PASTE: 'copy_paste',
    RIGHT_CLICK: 'right_click',
    DEVTOOLS: 'devtools',
    MULTIPLE_TABS: 'multiple_tabs',
    SCREENSHOT: 'screenshot',
    FOCUS_LOST: 'focus_lost',
    KEYBOARD_SHORTCUT: 'keyboard_shortcut'
};

interface CheatingEvent {
    type: string;
    details?: any;
    timestamp: Date;
}

// Module-level state
let testId: string | null = null;
let cheatingEvents: CheatingEvent[] = [];
let isMonitoring = false;
let tabSwitchCount = 0;
let focusLostCount = 0;
let lastFocusTime: Date | null = null;
let visibilityChangeHandler: (() => void) | null = null;
let blurHandler: (() => void) | null = null;
let focusHandler: (() => void) | null = null;
let contextMenuHandler: ((e: MouseEvent) => void) | null = null;
let keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
let pasteHandler: ((e: ClipboardEvent) => void) | null = null;
let copyHandler: ((e: ClipboardEvent) => void) | null = null;
let devtoolsInterval: NodeJS.Timeout | null = null;
let devtoolsOpen = false;

/**
 * Send cheating log to backend
 */
const sendCheatingLog = async (type: string, details?: any) => {
    if (!testId) return;

    try {
        const apiUrl = process.env.API_URL || '';
        const url = `${apiUrl}/api/code-run/test-logs/cheating`;

        // Use fetch with keepalive to ensure request completes even if page unloads
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                testId: testId,
                cheatingType: type,
                details: details || {},
                timestamp: new Date().toISOString()
            }),
            keepalive: true // Ensures request completes even if page closes
        }).catch(() => {
            // Silently fail
        });
    } catch (error) {
        // Silently fail
    }
};

/**
 * Log a cheating event
 */
const logCheating = (type: string, details?: any) => {
    if (!isMonitoring) return;

    const event: CheatingEvent = {
        type,
        details,
        timestamp: new Date()
    };

    cheatingEvents.push(event);

    // Send to backend (non-blocking)
    sendCheatingLog(type, details).catch(err => {
        // Silently fail - don't interrupt test
        console.error('Failed to log cheating event:', err);
    });
};

/**
 * Remove event listeners
 */
const removeEventListeners = () => {
    if (visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
        visibilityChangeHandler = null;
    }
    if (blurHandler) {
        window.removeEventListener('blur', blurHandler);
        blurHandler = null;
    }
    if (focusHandler) {
        window.removeEventListener('focus', focusHandler);
        focusHandler = null;
    }
    if (contextMenuHandler) {
        document.removeEventListener('contextmenu', contextMenuHandler);
        contextMenuHandler = null;
    }
    if (keyDownHandler) {
        document.removeEventListener('keydown', keyDownHandler);
        keyDownHandler = null;
    }
    if (pasteHandler) {
        document.removeEventListener('paste', pasteHandler);
        pasteHandler = null;
    }
    if (copyHandler) {
        document.removeEventListener('copy', copyHandler);
        copyHandler = null;
    }
    if (devtoolsInterval) {
        clearInterval(devtoolsInterval);
        devtoolsInterval = null;
    }
};

/**
 * Setup event listeners
 */
const setupEventListeners = () => {
    // Tab visibility change (tab switch)
    visibilityChangeHandler = () => {
        if (document.hidden) {
            tabSwitchCount++;
            logCheating(CHEATING_TYPES.TAB_SWITCH, {
                count: tabSwitchCount,
                timestamp: new Date().toISOString()
            });
        } else {
            lastFocusTime = new Date();
        }
    };
    document.addEventListener('visibilitychange', visibilityChangeHandler);

    // Window blur (focus lost)
    blurHandler = () => {
        focusLostCount++;
        logCheating(CHEATING_TYPES.FOCUS_LOST, {
            count: focusLostCount,
            timestamp: new Date().toISOString()
        });
    };
    window.addEventListener('blur', blurHandler);

    // Window focus
    focusHandler = () => {
        lastFocusTime = new Date();
    };
    window.addEventListener('focus', focusHandler);

    // Right click (context menu)
    contextMenuHandler = (e: MouseEvent) => {
        logCheating(CHEATING_TYPES.RIGHT_CLICK, {
            x: e.clientX,
            y: e.clientY,
            timestamp: new Date().toISOString()
        });
    };
    document.addEventListener('contextmenu', contextMenuHandler);

    // Keyboard shortcuts (Ctrl+C, Ctrl+V, Ctrl+A, F12, etc.)
    keyDownHandler = (e: KeyboardEvent) => {
        // Detect common cheating shortcuts
        if (e.ctrlKey || e.metaKey) {
            if (e.key === 'c' || e.key === 'C') {
                logCheating(CHEATING_TYPES.COPY_PASTE, {
                    action: 'copy',
                    timestamp: new Date().toISOString()
                });
            } else if (e.key === 'v' || e.key === 'V') {
                logCheating(CHEATING_TYPES.COPY_PASTE, {
                    action: 'paste',
                    timestamp: new Date().toISOString()
                });
            } else if (e.key === 'a' || e.key === 'A') {
                logCheating(CHEATING_TYPES.KEYBOARD_SHORTCUT, {
                    action: 'select_all',
                    timestamp: new Date().toISOString()
                });
            }
        }

        // F12 (DevTools)
        if (e.key === 'F12') {
            logCheating(CHEATING_TYPES.DEVTOOLS, {
                action: 'f12_pressed',
                timestamp: new Date().toISOString()
            });
        }

        // Ctrl+Shift+I (DevTools)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
            logCheating(CHEATING_TYPES.DEVTOOLS, {
                action: 'ctrl_shift_i',
                timestamp: new Date().toISOString()
            });
        }
    };
    document.addEventListener('keydown', keyDownHandler);

    // Paste detection
    pasteHandler = (e: ClipboardEvent) => {
        logCheating(CHEATING_TYPES.COPY_PASTE, {
            action: 'paste',
            hasData: e.clipboardData?.getData('text') ? true : false,
            timestamp: new Date().toISOString()
        });
    };
    document.addEventListener('paste', pasteHandler);

    // Copy detection
    copyHandler = (e: ClipboardEvent) => {
        logCheating(CHEATING_TYPES.COPY_PASTE, {
            action: 'copy',
            hasData: e.clipboardData?.getData('text') ? true : false,
            timestamp: new Date().toISOString()
        });
    };
    document.addEventListener('copy', copyHandler);

    // DevTools detection (check periodically)
    devtoolsOpen = false;
    const checkDevTools = () => {
        if (!isMonitoring) {
            if (devtoolsInterval) {
                clearInterval(devtoolsInterval);
                devtoolsInterval = null;
            }
            return;
        }

        const widthThreshold = window.outerWidth - window.innerWidth > 160;
        const heightThreshold = window.outerHeight - window.innerHeight > 160;

        if ((widthThreshold || heightThreshold) && !devtoolsOpen) {
            devtoolsOpen = true;
            logCheating(CHEATING_TYPES.DEVTOOLS, {
                action: 'devtools_opened',
                timestamp: new Date().toISOString()
            });
        } else if (!widthThreshold && !heightThreshold && devtoolsOpen) {
            devtoolsOpen = false;
        }
    };

    // Check for DevTools every 1 second
    devtoolsInterval = setInterval(checkDevTools, 1000);
};

/**
 * Start monitoring for a test
 */
export const startTestMonitoring = (testIdParam: string) => {
    if (isMonitoring) {
        stopTestMonitoring();
    }

    testId = testIdParam;
    isMonitoring = true;
    cheatingEvents = [];
    tabSwitchCount = 0;
    focusLostCount = 0;
    lastFocusTime = new Date();
    devtoolsOpen = false;

    setupEventListeners();
};

/**
 * Stop monitoring
 */
export const stopTestMonitoring = () => {
    isMonitoring = false;
    removeEventListeners();
    testId = null;
};

/**
 * Get cheating events summary
 */
export const getCheatingSummary = () => {
    return {
        totalEvents: cheatingEvents.length,
        tabSwitches: tabSwitchCount,
        focusLost: focusLostCount,
        events: cheatingEvents
    };
};
